import { Badge, Box, Divider, Group, Modal, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { BookOpen, CheckCircle, Clock, FileText, MessageCircle, RefreshCw } from 'react-feather';
import { RichTextContent } from '../../../../../components/common/RichTextEditor';
import type {
  SessionAgendaSection,
  SessionAgendaPreSessionWork,
} from '../../../../../services/api/sessionAgenda';

/**
 * MED-1232 (E1.3 / AC9) — Session Content preview.
 *
 * A CM can preview the content they are building AT ANY POINT — while it is in
 * progress and BEFORE groups are defined — and the preview reflects the authored
 * content regardless of complete/marked state.
 *
 * This renders the editor's CURRENT DRAFT directly (sections + participant
 * pre-session work), so it:
 *   - reflects unsaved edits (retiring MED-1231's "Save before previewing"
 *     dance — the preview is the live draft, nothing to lose),
 *   - works in by-number mode with no groups / no session row, and
 *   - is modality-agnostic and does NOT route through the live coach "Active
 *     Session" surface (which forced a concrete group session + facilitation
 *     type — the exact limitation AC9 removes).
 *
 * Read-only: no editing, no persistence, no live-session mutations.
 */

interface SessionContentPreviewModalProps {
  opened: boolean;
  onClose: () => void;
  topic: string | null;
  description: string;
  sections: SessionAgendaSection[];
  preSessionWork: SessionAgendaPreSessionWork;
  sessionDurationMinutes: number | null;
  /** Gates whether the Commitment Review line is meaningful to show (AC-CR2). */
  commitmentTrackingEnabled: boolean;
}

export function SessionContentPreviewModal({
  opened,
  onClose,
  topic,
  description,
  sections,
  preSessionWork,
  sessionDurationMinutes,
  commitmentTrackingEnabled,
}: SessionContentPreviewModalProps) {
  const sortedSections = sections.slice().sort((a, b) => a.sort_order - b.sort_order);
  const durationTotal = sortedSections.reduce((sum, s) => sum + (s.duration_minutes ?? 0), 0);

  const nonEmptyPrompts = preSessionWork.reflection_prompts
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .filter(p => p.prompt_text !== null && p.prompt_text.trim().length > 0);
  const hasReadingTasks =
    preSessionWork.reading_tasks !== null && preSessionWork.reading_tasks.trim().length > 0;
  const showCommitmentReview =
    commitmentTrackingEnabled && preSessionWork.commitment_review_enabled;
  const hasPreSessionWork = hasReadingTasks || nonEmptyPrompts.length > 0 || showCommitmentReview;

  const hasAnything =
    sortedSections.length > 0 || description.trim().length > 0 || hasPreSessionWork;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap={8} align="center">
          <ThemeIcon variant="light" color="medleyGreen" radius="xl" size="md">
            <BookOpen size={16} />
          </ThemeIcon>
          <Text fw={600}>Content preview</Text>
        </Group>
      }
      size="xl"
      centered
      scrollAreaComponent={undefined}
      data-testid="session-content-preview-modal"
    >
      <Stack gap="lg">
        {/* AC9 — a plain note that this is a live view of what's being authored,
            independent of groups and complete/marked state. */}
        <Group
          gap={8}
          align="flex-start"
          wrap="nowrap"
          c="dimmed"
          data-testid="session-content-preview-note"
        >
          <RefreshCw size={14} style={{ marginTop: 2, flexShrink: 0 }} />
          <Text size="sm">
            This preview reflects your current edits — including unsaved changes — and does not
            depend on groups being created or the content being marked complete.
          </Text>
        </Group>

        {!hasAnything && (
          <Text c="dimmed" data-testid="session-content-preview-empty">
            Nothing to preview yet. Add a session description, agenda sections, or pre-session work
            to see them here.
          </Text>
        )}

        {hasAnything && (
          <>
            {/* Session details */}
            <Stack gap={6}>
              <Title order={4}>{topic ?? 'Untitled session'}</Title>
              {description.trim().length > 0 && (
                <RichTextContent content={description} emptyText="" />
              )}
            </Stack>

            {/* Agenda */}
            {sortedSections.length > 0 && (
              <Stack gap="sm">
                <Group justify="space-between" align="center">
                  <Text fw={600} tt="uppercase" size="sm" c="dimmed" style={{ letterSpacing: 0.3 }}>
                    Agenda
                  </Text>
                  <Group gap={4} align="center" c="dimmed">
                    <Clock size={14} />
                    <Text size="sm" fw={500}>
                      {sessionDurationMinutes !== null
                        ? `${durationTotal} / ${sessionDurationMinutes} min`
                        : `${durationTotal} min`}
                    </Text>
                  </Group>
                </Group>

                <Box style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 12 }}>
                  {sortedSections.map((section, idx) => (
                    <PreviewSectionRow
                      key={section.id ?? idx}
                      section={section}
                      index={idx}
                      isLast={idx === sortedSections.length - 1}
                    />
                  ))}
                </Box>
              </Stack>
            )}

            {/* Participant pre-session work */}
            {hasPreSessionWork && (
              <>
                <Divider />
                <Stack gap="sm">
                  <Text fw={600} tt="uppercase" size="sm" c="dimmed" style={{ letterSpacing: 0.3 }}>
                    Participant pre-session work
                  </Text>

                  {showCommitmentReview && (
                    <Group gap={8} align="center" c="medleyGreen">
                      <CheckCircle size={16} />
                      <Text size="sm" fw={500}>
                        Participants review their prior-session commitment.
                      </Text>
                    </Group>
                  )}

                  {hasReadingTasks && (
                    <Stack gap={4}>
                      <Group gap={6} align="center">
                        <BookOpen size={14} />
                        <Text size="sm" fw={600}>
                          Reading / Tasks
                        </Text>
                      </Group>
                      <RichTextContent content={preSessionWork.reading_tasks} emptyText="" />
                    </Stack>
                  )}

                  {nonEmptyPrompts.length > 0 && (
                    <Stack gap={4}>
                      <Group gap={6} align="center">
                        <MessageCircle size={14} />
                        <Text size="sm" fw={600}>
                          Reflection prompts
                        </Text>
                      </Group>
                      <Stack gap={6}>
                        {nonEmptyPrompts.map((prompt, idx) => (
                          <Group key={prompt.id ?? idx} gap={8} align="flex-start" wrap="nowrap">
                            <Text size="sm" fw={600} c="dimmed">
                              {idx + 1}.
                            </Text>
                            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                              {prompt.prompt_text}
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
}

interface PreviewSectionRowProps {
  section: SessionAgendaSection;
  index: number;
  isLast: boolean;
}

function PreviewSectionRow({ section, index, isLast }: PreviewSectionRowProps) {
  const sortedPrompts = section.leader_prompts.slice().sort((a, b) => a.sort_order - b.sort_order);
  const sortedVisuals = section.visuals.slice().sort((a, b) => a.sort_order - b.sort_order);
  const hasParticipantPrompt =
    section.participant_prompt !== null && section.participant_prompt.trim().length > 0;

  return (
    <Box
      style={{
        padding: 'var(--mantine-spacing-md) var(--mantine-spacing-lg)',
        borderBottom: isLast ? undefined : '1px solid var(--mantine-color-gray-3)',
      }}
    >
      <Group justify="space-between" align="center" mb={sortedPrompts.length || sortedVisuals.length || hasParticipantPrompt ? 'sm' : 0}>
        <Group align="center" gap="sm">
          <ThemeIcon variant="light" color="dark" radius="xl" size="md">
            <Text size="sm" fw={700}>
              {index + 1}
            </Text>
          </ThemeIcon>
          <Text fw={600}>{section.section_name?.trim() || 'Untitled section'}</Text>
        </Group>
        {section.duration_minutes !== null && (
          <Group gap={4} align="center" c="dimmed">
            <Clock size={14} />
            <Text size="sm">{section.duration_minutes} min</Text>
          </Group>
        )}
      </Group>

      {sortedPrompts.length > 0 && (
        <Stack gap="xs" mb={sortedVisuals.length || hasParticipantPrompt ? 'sm' : 0}>
          {sortedPrompts.map((prompt, promptIdx) => {
            const hasIntro = !!prompt.intro_text && prompt.intro_text.trim().length > 0;
            const hasBody =
              !!prompt.discussion_prompt && prompt.discussion_prompt.trim().length > 0;
            if (!hasIntro && !hasBody) return null;
            return (
              <div key={promptIdx}>
                {hasIntro && (
                  <Text size="xs" c="dimmed" fw={600} mb={4}>
                    {prompt.intro_text!.trim()}
                  </Text>
                )}
                {hasBody && <RichTextContent content={prompt.discussion_prompt} emptyText="" />}
              </div>
            );
          })}
        </Stack>
      )}

      {sortedVisuals.length > 0 && (
        <Group gap="xs" align="center" mb={hasParticipantPrompt ? 'sm' : 0}>
          {sortedVisuals.map(v => (
            <Badge
              key={v.slide_page}
              variant="light"
              color="gray"
              radius="xl"
              leftSection={<FileText size={10} />}
            >
              Slide {v.slide_page}
            </Badge>
          ))}
        </Group>
      )}

      {hasParticipantPrompt && (
        <Box
          style={{
            background: 'var(--mantine-color-gray-0)',
            borderRadius: 8,
            padding: 'var(--mantine-spacing-sm)',
          }}
        >
          <Group gap={6} align="center" mb={4}>
            <MessageCircle size={12} />
            <Text size="xs" fw={600} c="dimmed">
              What participants see
            </Text>
          </Group>
          <RichTextContent content={section.participant_prompt} emptyText="" />
        </Box>
      )}
    </Box>
  );
}

export default SessionContentPreviewModal;
